import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useToast } from '../components/Toast';

export default function JoinPage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, token, groups } = useAuthStore();
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'admin') {
      navigate('/admin');
      return;
    }

    // Store in session storage if not logged in
    if (!token) {
      sessionStorage.setItem('pendingJoinCode', code);
      setLoading(false);
      navigate('/login');
      return;
    }

    const fetchGroup = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/api/groups/preview/${code}`);
        if (res.status === 404) {
          setGroup('not_found');
          return;
        }
        const data = await res.json();
        setGroup(data.data);
      } catch {
        setGroup('not_found');
      } finally {
        setLoading(false);
      }
    };
    fetchGroup();
  }, [code, token, navigate]);

  const handleJoin = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/api/groups/join/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ joinCode: code })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Join request sent!');
        navigate('/onboarding');
      } else {
        toast.error(data.error);
      }
    } catch {
      toast.error('Failed to send join request');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-2 border-indigo-400 border-t-transparent rounded-full" /></div>;

  if (group === 'not_found') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="text-center text-white">
          <div className="text-6xl mb-4">📖</div>
          <h1 className="text-2xl font-bold mb-2">Group not found</h1>
          <p className="text-white/60 mb-6">This join code doesn't exist or the group is not accepting members.</p>
          <button onClick={() => navigate('/')} className="btn-primary">Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[#090e1a]">
      <div className="glass-strong rounded-2xl p-8 max-w-md w-full text-center">
        <div className="text-4xl mb-4">📖</div>
        <h1 className="text-2xl heading font-bold text-white mb-2">{group.name}</h1>
        <p className="text-white/60 text-sm mb-6">Leader: {group.leaderName || 'Unknown'} · {group.memberCount}/{group.maxMembers} Members</p>
        
        {groups?.length >= 3 ? (
          <div>
            <p className="text-amber-400 text-sm mb-4">You are already in 3 groups (maximum). Leave one first to join another.</p>
            <button onClick={() => navigate('/dashboard')} className="btn-primary w-full">Go to Dashboard</button>
          </div>
        ) : (
          <button onClick={handleJoin} className="btn-primary w-full">Request to Join</button>
        )}
      </div>
    </div>
  );
}
